import { createContext, useState, useContext, useEffect, useCallback, type ReactNode } from 'react';
import api from './api';
import { authService } from './authService';
import { ENDPOINTS } from '../config';
import type { User } from '../types'; // ✅ FIX: Added 'type' keyword

interface AuthContextType {
  isLoggedIn: boolean;
  user: User | null;
  login: (token: string) => Promise<User | null>;
  loginWithPhone: (phone: string, password: string) => Promise<User | null>;
  logout: () => void;
  fetchUser: () => Promise<User | null>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(authService.isAuthenticated());
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async (): Promise<User | null> => {
    if (!authService.isAuthenticated()) {
      setUser(null);
      setIsLoggedIn(false);
      setIsLoading(false);
      return null;
    }
    try {
      const response = await api.get<User>(ENDPOINTS.GET_ME);
      setUser(response.data);
      setIsLoggedIn(true);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch user. Session may be invalid.", error);
      logout();
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (token: string): Promise<User | null> => {
    setIsLoading(true);
    authService.setToken(token);
    setIsLoggedIn(true);
    return await fetchUser();
  };

  const loginWithPhone = async (phone: string, password: string): Promise<User | null> => {
    setIsLoading(true);
    try {
      // Partners use phone as username on the standard token endpoint
      const response = await api.post(ENDPOINTS.LOGIN, 
        new URLSearchParams({ username: phone, password }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );
      
      if (response.data?.access_token) {
        authService.setToken(response.data.access_token);
        setIsLoggedIn(true);
        return await fetchUser();
      } else {
        throw new Error('No access token received');
      }
    } catch (error: any) {
      setIsLoading(false);
      const errorMsg = error.response?.data?.detail || 
                       error.response?.data?.message || 
                       error.message ||
                       'Phone login failed';
      console.error("Login Error:", error.response?.data || error);
      throw new Error(errorMsg);
    }
  };

  const logout = () => {
    authService.clearToken();
    setUser(null);
    setIsLoggedIn(false);
    if (window.location.pathname.startsWith('/admin') || window.location.pathname.startsWith('/profile')) {
      window.location.href = '/';
    }
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, user, login, loginWithPhone, logout, fetchUser, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};


export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
